import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { Pageable, WrapperPageableResponse, WrapperResponse, PaymentLinkFilterSpec } from './service-common';

export interface CustomerDTO {
  id: number;
  taxId: string;
  legalName: string;
  comercialName: string;
  type?: string;
  email?: string;
  phone?: string;
}

/** Moneda (uso en PaymentDTO). */
export interface CurrencyDTO {
  id: number;
  name?: string;
  code?: string;
}

/** Pago asociado a una liga (detalle desde GET /payment-links/{id}). */
export interface PaymentDTO {
  id: number;
  paymentProvider?: string;
  amount?: number;
  amountFee?: number;
  amountNet?: number;
  currency?: CurrencyDTO;
  status?: PaymentLinkStatus;
  paymentForm?: { id?: number; code?: string; name?: string; description?: string };
  providerId?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Resumen de factura asociada (viene en GET /payment-links/{id}). */
export interface PaymentLinkInvoiceDTO {
  id: number;
  number?: string;
  series?: string;
  status?: string;
  total?: number;
  amountDue?: number;
  currency?: string;
  customer?: { comercialName?: string; legalName?: string };
}

export interface PaymentLinkDTO {
  id: number;
  customer?: CustomerDTO;
  expiresAt?: string;
  link?: string;
  concept?: string;
  provider?: 'PAYCODE';
  providerId?: string;
  trackingId?: string;
  totalAmount?: number;
  redirectUrl?: string;
  status?: PaymentLinkStatus;
  paidAt?: string;
  createdAt?: string;
  updatedAt?: string;
  /** Solo presente en detalle (GET /payment-links/{id}). */
  payment?: PaymentDTO;
  /** Factura asociada a la liga (solo en detalle por id). */
  invoice?: PaymentLinkInvoiceDTO;
}

export type PaymentLinkStatus = 'PAID' | 'CANCELLED' | 'FAILED' | 'PENDING' | 'EXPIRED';

/** Línea del resumen de factura (página de éxito). */
export interface PaymentSuccessSummaryLineDTO {
  description: string;
  quantity: number;
  unitPrice: number;
  totalAmount: number;
}

/** Resumen de factura pagada para la página pública de éxito (sin auth). */
export interface PaymentSuccessSummaryDTO {
  invoiceId?: number;
  invoiceNumber: string;
  series?: string;
  concept: string;
  totalAmount: number;
  customerName: string;
  customerTaxId?: string;
  currency: string;
  paidAt?: string;
  numberOfPayments?: number;
  paymentNumber?: number;
  lines?: PaymentSuccessSummaryLineDTO[];
  paymentStatus?: string;
}

export type WrapperPageableResponsePaymentLinkDTO = WrapperPageableResponse<PaymentLinkDTO>;
export type WrapperResponsePaymentSuccessSummaryDTO = WrapperResponse<PaymentSuccessSummaryDTO>;

@Injectable({
  providedIn: 'root'
})
export class PaymentLinkService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  searchPaymentLinks(
    spec: PaymentLinkFilterSpec = {},
    pageable: Pageable = { page: 0, size: 20 }
  ): Observable<WrapperPageableResponsePaymentLinkDTO> {
    let params = new HttpParams();

    Object.entries(spec).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        if (value.length > 0) {
          value.forEach(v => params = params.append(key, String(v)));
        }
      } else if (value !== '') {
        params = params.append(key, String(value));
      }
    });

    params = params.append('page', String(pageable.page));
    params = params.append('size', String(pageable.size));

    if (pageable.sort && pageable.sort.length > 0) {
      pageable.sort.forEach(sort => params = params.append('sort', sort));
    }

    return this.http.get<WrapperPageableResponsePaymentLinkDTO>(`${this.apiUrl}/payment-links`, { params });
  }

  listPaymentLinks(
    spec: PaymentLinkFilterSpec = {},
    pageable: Pageable = { page: 0, size: 20 }
  ): Observable<WrapperPageableResponsePaymentLinkDTO> {
    let params = new HttpParams();

    Object.entries(spec).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        if (value.length > 0) {
          value.forEach(v => params = params.append(key, String(v)));
        }
      } else if (value !== '') {
        params = params.append(key, String(value));
      }
    });

    params = params.append('page', String(pageable.page));
    params = params.append('size', String(pageable.size));

    if (pageable.sort && pageable.sort.length > 0) {
      pageable.sort.forEach(sort => params = params.append('sort', sort));
    }

    return this.http.get<WrapperPageableResponsePaymentLinkDTO>(`${this.apiUrl}/payment-links/list`, { params });
  }

  /**
   * Obtiene el detalle de una liga de pago por ID (incluye payment si existe).
   * Según api-doc: GET /payment-links/{id}.
   */
  getPaymentLinkById(id: number): Observable<WrapperResponse<PaymentLinkDTO>> {
    return this.http.get<WrapperResponse<PaymentLinkDTO>>(`${this.apiUrl}/payment-links/${id}`);
  }

  /**
   * Obtiene el resumen de la factura pagada para la página de éxito (endpoint público).
   */
  getPaymentSuccessSummary(
    trackingId: string
  ): Observable<WrapperResponsePaymentSuccessSummaryDTO> {
    const params = new HttpParams()
      .set('trackingId', trackingId);
    return this.http.get<WrapperResponsePaymentSuccessSummaryDTO>(
      `${this.apiUrl}/public/payment-success`,
      { params }
    );
  }
}
